import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import GridCard from '@Guidelines/Component/GridCard';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import WorkflowRunCardActions from '@UI/Pages/WorkflowRuns/WorkflowRunCard/WorkflowRunCardActions';
import WorkflowRunStatusTag from '@UI/Pages/WorkflowRuns/WorkflowRunCard/WorkflowRunStatusTag';
import type FacilityWorkflowRunView from '@UI/Store/WorkflowRuns/Views/FacilityWorkflowRunView';
import { css } from '@emotion/react';
import { ArrowRight } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Tag } from '@verclam/ui-kit';
import useSubjectTypes from '@UI/Hooks/useSubjectTypes';
import type SubjectDirectoryListView from '@UI/Store/Subjects/Views/SubjectDirectoryListView';
import type BeneficiaryDirectoryListView from '@UI/Store/Beneficiaries/Views/BeneficiaryDirectoryListView';

// A single module-level formatter: `Intl.DateTimeFormat` construction is costly, and the instance is
// stateless and reusable across every call. `fr-FR` already renders "{date} à {heure}" on its own.
const instantFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' });

const CARD_ROW_GAP = '0.625rem';

/**
 * The card's own box, not its shell: `position: relative` anchors the name link's full-card overlay
 * (see `nameLinkStyle`), `:hover` and `:has(a:focus-visible)` read the card's hairline and focus ring.
 * Passed to `GridCard`'s `css` prop, which resolves to `className` and merges into the shell's own
 * rule — padding, radius, surface and border stay `GridCard`'s property and are never redeclared here.
 */
const cardBoxStyle = css`
	position: relative;
	transition: border-color ${theme.motion.duration};

	&:hover {
		border-color: ${theme.colors.borderStrong};
	}

	/* The overlay carries the pointer, so the focus ring has to be drawn by the card it covers. */
	&:has(a:focus-visible) {
		outline: 2px solid ${theme.colors.accent};
		outline-offset: 2px;
	}
`;

const headerRowStyle = css`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 0.75rem;
`;

const nameStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const kindRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const runLabelStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const stepLineStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const timestampStyle = css`
	margin: 0;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkFaint};
`;

const openLinkStyle = css`
	align-self: flex-start;
	display: inline-flex;
	align-items: center;
	gap: 0.375rem;
	margin-top: 0.125rem;
	font-family: ${theme.fonts.body};
	font-weight: ${theme.fontWeights.medium};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.accentDark};
	text-decoration: none;

	&:hover,
	&:focus-visible {
		text-decoration: underline;
	}

`;

const nameLinkStyle = css`
	${openLinkStyle}

	&::after {
		content: '';
		position: absolute;
		// same as top/left/right/bottom: 0, but more explicit for the sake of clarity
		inset: 0;
		border-radius: inherit;
	}
`;

// Above the name link's full-card overlay, so the buttons stay clickable.
const actionsRowStyle = css`
	position: relative;
	z-index: 1;
	align-self: flex-start;
`;

type Props = {
	readonly run: FacilityWorkflowRunView;
	readonly beneficiaries: BeneficiaryDirectoryListView | null;
	readonly subjects: SubjectDirectoryListView | null;
};

/**
 * One row of the supervision list. Renders exactly what the server decided (`status`, `focusStep`) —
 * no threshold, no status recomputed here. `layout="stacked"`: cards in this grid are not aligned on a
 * shared subgrid row, since their optional lines (focus step, "Terminé le …") make a fixed track
 * count awkward for no visual benefit at this card's size. The card opens the preparation (proposed
 * run) or the interview (automatic run); handling is delegated to `WorkflowRunCardActions`.
 */
const WorkflowRunCard: React.FC<Props> = ({ run, beneficiaries, subjects }) => {
	const { types } = useSubjectTypes();

	const subjectName = React.useMemo(() => {
		if (run.beneficiaryId !== null) {
			return beneficiaries?.nameOf(run.beneficiaryId) || `Bénéficiaire n° ${run.beneficiaryId}`;
		}

		return subjects?.titleOf(run.subjectId) || `Dossier n° ${run.subjectId}`;
	}, [beneficiaries, subjects, run]);

	const focusStep = run.focusStep;

	// A dossier the directory cannot resolve to a known type has no address: no link, no action.
	const subjectAddress = React.useMemo(() => {
		if (run.beneficiaryId !== null) {
			return SubjectAddress.forBeneficiary(run.beneficiaryId);
		}

		const typeId = subjects?.typeIdOf(run.subjectId) ?? null;
		const type = typeId === null ? null : (types?.findById(typeId) ?? null);

		return type === null ? null : new SubjectAddress(type.key, run.subjectId);
	}, [subjects, types, run]);

	const workflowRunPath = React.useMemo(() => {
		if (subjectAddress === null) {
			return null;
		}

		const dossierPath = buildSubjectPath(subjectAddress);

		if (run.isProposal) {
			return `${dossierPath}/preparations/${run.id}`;
		}

		return run.interviewId === null ? null : `${dossierPath}/interviews/${run.interviewId}`;
	}, [subjectAddress, run]);

	const runTypeSeverity = React.useMemo(() => {
		switch (run.kind.value) {
			case 'interview':
				return undefined;
			case 'notes':
				return 'info';
			case 'documents':
				return 'success';
			default:
				return 'secondary';
		}
	}, [run.kind.value]);

	return (
		<GridCard layout="stacked" rowGap={CARD_ROW_GAP} css={cardBoxStyle}>
			<div css={headerRowStyle}>
				<ConditionalLoader data={workflowRunPath} fallback={<p css={nameStyle}>{subjectName}</p>}>
					{(path) => (
						<Link css={nameLinkStyle} to={path}>
							<p css={nameStyle}>{subjectName}</p>
						</Link>
					)}
				</ConditionalLoader>
				<WorkflowRunStatusTag status={run.status} />
			</div>
			<div css={kindRowStyle}>
				<Tag value={run.kind.label} severity={runTypeSeverity} rounded />
				<p css={runLabelStyle}>{run.label}</p>
			</div>
			<ConditionalLoader data={focusStep}>
				{(step) => (
					<p
						css={stepLineStyle}
					>{`${step.label} — ${step.isStalled ? 'Bloqué' : step.status.label}`}</p>
				)}
			</ConditionalLoader>
			<p css={timestampStyle}>{`Lancé le ${instantFormatter.format(run.startedAt)}`}</p>
			<ConditionalLoader data={run.finishedAt}>
				{(finishedAt) => (
					<p css={timestampStyle}>{`Terminé le ${instantFormatter.format(finishedAt)}`}</p>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={run.handledAt}>
				{(handledAt) => (
					<p
						css={timestampStyle}
					>{`Marqué comme traité le ${instantFormatter.format(handledAt)}`}</p>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={workflowRunPath}>
				{(path) => (
					<Link css={openLinkStyle} to={path}>
						Ouvrir le traitement
						<ArrowRight size={14} />
					</Link>
				)}
			</ConditionalLoader>
			<div css={actionsRowStyle}>
				<ConditionalLoader data={subjectAddress}>
					{(address) => <WorkflowRunCardActions run={run} subject={address} />}
				</ConditionalLoader>
			</div>
		</GridCard>
	);
};

export default WorkflowRunCard;
